import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { authApi } from '../api/endpoints.ts';

/**
 * The bar across the top of the shared public demo.
 *
 * FOUR THINGS, and the order is the order somebody needs them in.
 *
 *  1. THIS IS A DEMO, AND IT IS SHARED. The single worst outcome of the whole
 *     feature is a cleaning company believing they have set up their account
 *     and beginning to put real customers in it. The notice is permanent, at
 *     the top of every page, and it says both halves: that this is not their
 *     account, and that other people are in it too.
 *
 *  2. WHAT THEIR CREDITS ARE DOING. An AI assist that silently stops working
 *     reads as a broken feature. A meter that says "63 of 75 left" reads as a
 *     budget, which is what it is — and lets somebody decide to spend twelve
 *     on a walkthrough rather than discovering afterwards that they could not.
 *
 *  3. WHEN IT ENDS. Two clocks, because they mean different things: this visit
 *     ends in a couple of hours, and the whole company is refurnished weekly.
 *     Somebody who does not know the second will come back on Thursday
 *     expecting to find the quote they built on Monday.
 *
 *  4. HOW TO STOP BEING IN A DEMO. The call to action is the point of all of
 *     it, and it is a button rather than a sentence.
 *
 * Renders nothing at all for an ordinary session — `publicDemo` is null, which
 * is the first thing this reads.
 */
export default function PublicDemoBanner() {
  const { t } = useTranslation();
  const { publicDemo: fromSession } = useSession();
  const [now, setNow] = useState(() => Date.now());

  const active = fromSession !== null;

  /**
   * The credit balance, kept current.
   *
   * The session response carries one, and on its own that is not enough: the
   * session query is cached, so a visitor could spend twelve credits on a
   * walkthrough and watch the banner go on claiming seventy-five — and
   * eventually see credits "remaining" while the server was already refusing
   * them, which reads as a broken feature rather than a spent budget.
   *
   * So the banner re-reads the session for itself, under its OWN query key, on
   * a timer and on every window focus. Its own key rather than an invalidation
   * of the shared session query: every AI mutation in the app would otherwise
   * have to remember to invalidate a key it has nothing to do with, and the
   * one that forgot would quietly bring the stale number back. The shared
   * session cache is left exactly as it was.
   *
   * `refetchInterval` is what actually keeps it moving, and `staleTime` is not
   * a substitute for it: a stale query is refetched when something asks for it
   * again, and nothing does while the visitor stays on one screen spending
   * credits — which is precisely the case this exists for. The poll pauses
   * while the tab is in the background (the default) and stops with the
   * banner, so it costs one small request a minute for two hours at most.
   */
  const live = useQuery({
    queryKey: ['public-demo-state'],
    queryFn: () => authApi.session(),
    enabled: active,
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // A countdown that only moves on navigation is a countdown somebody watches
  // being wrong. One tick a minute is enough for a two-hour clock and costs
  // nothing; the effect is skipped entirely outside the demo.
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, [active]);

  if (!fromSession) return null;

  // The freshest answer available, falling back to the session's copy until
  // the first fetch lands — so the banner never renders without a balance.
  const publicDemo = live.data?.publicDemo ?? fromSession;

  const minutesLeft = Math.max(0, Math.round((Date.parse(publicDemo.endsAt) - now) / 60_000));
  const credits = publicDemo.credits;
  // Below a fifth of the allowance the meter changes colour, because by then
  // it has stopped being information and started being a warning.
  const creditsLow = credits.remaining <= Math.ceil(credits.allowance / 5);
  const resetDays = publicDemo.resetsAt
    ? Math.max(0, Math.ceil((Date.parse(publicDemo.resetsAt) - now) / 86_400_000))
    : null;

  return (
    <div className="border-b-4 border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] px-4 py-3 print:hidden">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold tracking-wide text-[color:var(--color-brand-strong)] uppercase">
            {t('publicDemo.bannerTitle')}
          </p>
          <p className="text-sm text-[color:var(--color-brand-strong)]">{t('publicDemo.bannerBody')}</p>
          <p className="mt-1 text-xs text-[color:var(--color-brand-strong)]">
            {t('publicDemo.bannerClocks', { count: minutesLeft })}
            {resetDays === null ? '' : ` · ${t('publicDemo.bannerReset', { count: resetDays })}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div
            className={`rounded-lg border px-3 py-2 text-center ${
              creditsLow
                ? 'border-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)] text-[color:var(--color-warning)]'
                : 'border-[color:var(--color-line)] bg-[color:var(--color-surface)] text-[color:var(--color-brand-strong)]'
            }`}
          >
            <p className="text-lg leading-none font-bold">{credits.remaining}</p>
            <p className="text-[0.7rem] tracking-wide uppercase">
              {t('publicDemo.creditsLeft', { allowance: credits.allowance })}
            </p>
          </div>
          <a className="btn btn-primary" href={publicDemo.signupUrl}>
            {t('publicDemo.startTrial')}
          </a>
        </div>
      </div>
    </div>
  );
}
